import { Link, useParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { BriefcaseIcon, CalendarIcon, ChevronLeftIcon, MapPinIcon } from 'lucide-react';

import { useLanguage } from '@/contexts/language-context';
import { useCurrentBusiness } from '@/hooks/use-current-business';
import {
  getJob,
  getJobVisit,
  jobQueryKey,
  jobVisitQueryKey,
} from '@/lib/api/jobs';

import {
  formatDate,
  formatVisitTimeRange,
  statusColorClass,
  visitStatusLabel,
} from '../jobs/helpers';
import { JobAttachmentsSection } from '../jobs/job-attachments-section';
import { JobNotesSection } from '../jobs/job-notes-section';
import { VisitStatusAction } from './visit-status-action';

export function EmployeeVisitDetail() {
  const { t } = useLanguage();
  const { jobId = '', visitId = '' } = useParams();
  const business = useCurrentBusiness();
  const businessId = business?.business_id;

  const ready = Boolean(businessId && jobId && visitId);

  const jobQuery = useQuery({
    queryKey: jobQueryKey(businessId ?? '', jobId),
    queryFn: () => getJob(businessId!, jobId),
    enabled: ready,
  });

  const visitQuery = useQuery({
    queryKey: jobVisitQueryKey(businessId ?? '', jobId, visitId),
    queryFn: () => getJobVisit(businessId!, jobId, visitId),
    enabled: ready,
  });

  const job = jobQuery.data;
  const visit = visitQuery.data;
  const isLoading = jobQuery.isPending || visitQuery.isPending;
  const isError = jobQuery.isError || visitQuery.isError;

  return (
    <div className="max-w-2xl mx-auto flex flex-col gap-5">
      <Link
        to="/schedule"
        className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 w-fit"
        data-testid="employee-visit-back"
      >
        <ChevronLeftIcon className="w-3.5 h-3.5" />
        {t.employeeScheduleBack}
      </Link>

      {isLoading && <p className="text-sm text-muted-foreground py-10">{t.loading}</p>}

      {isError && <p className="text-sm text-destructive">{t.noJobsFound}</p>}

      {job && visit && (
        <div className="bg-card border rounded-xl p-5 flex flex-col gap-4">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                <BriefcaseIcon className="w-5 h-5 text-primary" />
              </div>
              <h1 className="text-lg font-semibold leading-tight truncate">
                {job.title}
              </h1>
            </div>
            <span
              className={`text-[11px] font-medium px-2 py-0.5 rounded-full shrink-0 ${statusColorClass(
                visit.status,
              )}`}
            >
              {visitStatusLabel(visit.status, t)}
            </span>
          </div>

          <div className="flex flex-col gap-1.5 text-sm text-muted-foreground">
            <div className="flex items-center gap-1.5">
              <CalendarIcon className="w-3.5 h-3.5 shrink-0" />
              <span>
                {formatDate(visit.scheduled_date)} · {formatVisitTimeRange(visit)}
              </span>
            </div>
            {job.service_address && (
              <div className="flex items-start gap-1.5">
                <MapPinIcon className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                <span>{job.service_address}</span>
              </div>
            )}
          </div>

          {job.description && (
            <p className="text-sm bg-muted/60 rounded px-3 py-2">{job.description}</p>
          )}

          <div>
            <VisitStatusAction
              businessId={businessId!}
              jobId={jobId}
              visitId={visitId}
              status={visit.status}
            />
          </div>
        </div>
      )}

      {job && businessId && (
        <div className="rounded-xl border bg-card overflow-hidden">
          <JobNotesSection businessId={businessId} jobId={jobId} />
          <JobAttachmentsSection businessId={businessId} jobId={jobId} />
        </div>
      )}
    </div>
  );
}
